import React, {
  useState,
  useMemo,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CurrentUserContext } from "../../../context/CurrentUserContext";
import { useCalendarClient } from "../../../context/ApiContext";

import moment from "moment";
import "moment-timezone";
import LoadingSpinner from "./LoadingSpinner";
import { getTimeDurationInMinutes } from "./Utility";
import {
  getEvents,
  getMeetingRoomDetails,
} from "../../../services/calendar/Calendar";
import NavigationCalendar from "./NavigationCalendar";
import EventTile from "./EventTile";
import EventDetailCard from "./EventDetailCard";
import { listOccurrenceStartsMs, toUtcIsoString } from "./meetingOccurrences";

interface CalendarEvent {
  Id: any;
  Category: string;
  Title: string;
  StartTime: string | undefined;
  EndTime: string | undefined;
  Color: string | null;
  Location: any;
  DurationInMinutes: any;
  MeetingLink: any;
  IsAllDay: boolean;
  PBSLiveEvent: boolean;
  IsBroadCast: boolean;
  Description: any;
  DetailsJSON: any;
  IsRecurring?: boolean;
  Recurring?: any;
}

// The window fetchEvents loads: today +/- 12 months (local).
const getEventsDateRange = () => {
  const today = new Date();
  const fromDate = new Date(today);
  fromDate.setMonth(today.getMonth() - 12);
  const toDate = new Date(today);
  toDate.setMonth(today.getMonth() + 12);
  return { fromDate, toDate };
};

// Expand recurring events into individual occurrences inside the fetched window. Every
// occurrence keeps the organizer's wall-clock time of day (see meetingOccurrences.ts), and
// cancelled occurrences (ExcludedDates) are dropped. All date strings are read as UTC here,
// before any computation, so the rest of the widget can parse them without a zone guess.
const expandRecurringEvents = (events: any[]): any[] => {
  const expandedEvents: any[] = [];
  const { fromDate, toDate } = getEventsDateRange();
  fromDate.setHours(0, 0, 0, 0);
  toDate.setHours(23, 59, 59, 999);

  events.forEach((rawEvent) => {
    const event = {
      ...rawEvent,
      StartTime: toUtcIsoString(rawEvent.StartTime),
      EndTime: toUtcIsoString(rawEvent.EndTime),
      RecurringStart: toUtcIsoString(rawEvent.RecurringStart),
      RecurringUntil: toUtcIsoString(rawEvent.RecurringUntil),
    };

    if (!event.IsRecurring || !event.RecurringStart || !event.RecurringUntil) {
      expandedEvents.push(event);
      return;
    }

    // Use DurationInMinutes from API instead of EndTime (which is RecurringUntil for recurring events)
    const durationMs = (event.DurationInMinutes || 0) * 60 * 1000;

    listOccurrenceStartsMs(event, fromDate.getTime(), toDate.getTime()).forEach(
      (occurrenceMs) => {
        expandedEvents.push({
          ...event,
          StartTime: new Date(occurrenceMs).toISOString(),
          EndTime: new Date(occurrenceMs + durationMs).toISOString(),
          IsRecurring: true,
        });
      },
    );
  });

  return expandedEvents;
};

const Calendar = () => {
  const { currentUser } = useContext(CurrentUserContext);
  const calendarClient = useCalendarClient();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null,
  );
  const [meetingRoomDetails, setMeetingRoomDetails] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const isMobile = screenWidth <= 780;
  const isTablet = screenWidth >= 781 && screenWidth <= 1024;

  const fullCalendarEvents = useMemo(() => {
    // First expand recurring events
    const expandedEvents = expandRecurringEvents(events);

    return expandedEvents.map((event) => {
      const start = moment.utc(event.StartTime).local();
      const end = moment.utc(event.EndTime).local();
      const durationInMinutes = getTimeDurationInMinutes(start, end);

      let durationDisplay: string;
      if (event.IsAllDay) {
        durationDisplay = "All Day";
      } else if (durationInMinutes >= 60) {
        const hours = durationInMinutes / 60;
        durationDisplay =
          hours % 1 === 0 ? `${hours}h` : `${hours.toFixed(1)}h`;
      } else {
        durationDisplay = `${durationInMinutes} mins`;
      }

      let locationDisplay: string;
      if (event.IsBroadCast) {
        locationDisplay = "PBS Broadcast";
      } else if (event.PBSLiveEvent) {
        locationDisplay = "PBS Live";
      } else if (event.Category === "Telehealth") {
        locationDisplay = "PBS Telehealth";
      } else {
        locationDisplay = event.Location;
      }

      return {
        Id: event.Id,
        Category: event.Category,
        Title: event.Title,
        StartTime: event.StartTime,
        EndTime: event.EndTime,
        Location: locationDisplay,
        Color: event.Color,
        DurationInMinutes: durationDisplay,
        MeetingLink: event.MeetingLink,
        IsAllDay: event.IsAllDay,
        PBSLiveEvent: event.PBSLiveEvent,
        IsBroadCast: event.IsBroadCast,
        DetailsJSON: event.DetailsJSON,
        Description: event.Description,
        IsRecurring: event.IsRecurring,
        Recurring: event.Recurring,
        UTCDiff: event.UTCDiff,
        UTCDiffEndTime: event.UTCDiffEndTime,
      };
    });
  }, [events]);

  const sortedEvents = useMemo(() => {
    return [...fullCalendarEvents].sort(
      (a, b) =>
        new Date(a.StartTime!).getTime() - new Date(b.StartTime!).getTime(),
    );
  }, [fullCalendarEvents]);

  const displayEvents = useMemo(() => {
    const selectedDateStart = new Date(selectedDate);
    selectedDateStart.setHours(0, 0, 0, 0);

    // Filter events for the selected date
    const eventsForSelectedDate = sortedEvents.filter((event) => {
      const eventDate = new Date(event.StartTime!);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate.getTime() === selectedDateStart.getTime();
    });

    if (eventsForSelectedDate.length >= 4)
      return eventsForSelectedDate.slice(0, 4);

    const remainingSlots = 4 - eventsForSelectedDate.length;
    const upcomingEvents = sortedEvents.filter((event) => {
      const eventDate = new Date(event.StartTime!);
      eventDate.setHours(0, 0, 0, 0);

      return (
        eventDate >= selectedDateStart &&
        eventDate.getTime() !== selectedDateStart.getTime()
      );
    });

    return [
      ...eventsForSelectedDate,
      ...upcomingEvents.slice(0, remainingSlots),
    ].slice(0, 4);
  }, [selectedDate, sortedEvents]);

  const groupedEvents = useMemo(() => {
    const groups: { [key: string]: CalendarEvent[] } = {};
    displayEvents.forEach((event) => {
      const dateKey = new Date(event.StartTime!).toDateString();
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(event);
    });
    return groups;
  }, [displayEvents]);

  const handleJoinMeeting = (event: {
    MeetingLink?: string;
    Category?: string;
  }) => {
    if (event.MeetingLink) {
      window.open(event.MeetingLink, "_blank", "noopener,noreferrer");
    } else {
      alert("No meeting link available.");
    }
  };

  const fetchMeetingRoomDetails = useCallback(
    async (event?: { MeetingLink?: string; StartTime?: string }) => {
      if (!event?.MeetingLink || !event?.StartTime) {
        setMeetingRoomDetails(null);
        return;
      }
      try {
        const response = await getMeetingRoomDetails(
          event.MeetingLink,
          moment.utc(event.StartTime).format("YYYY-MM-DD"),
          calendarClient,
        );
        setMeetingRoomDetails(response.data);
      } catch (error) {
        setMeetingRoomDetails([]);
      }
    },
    [calendarClient],
  );

  const handleBusinessFormClick = async (event: { StartTime?: string }) => {
    try {
      const formURL = meetingRoomDetails?.customProperties?.formURL;
      if (!formURL) {
        console.log("Business form URL not available for this meeting.");
        return;
      }
      const eventDate = moment.utc(event.StartTime).format("YYYY-MM-DD");
      const finalUrl = formURL.includes("eventDate=")
        ? formURL
        : `${formURL}&eventDate=${eventDate}`;
      window.open(finalUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.log("Error fetching business form:", error);
    }
  };

  const handleEventCardClick = (event: CalendarEvent) =>
    setSelectedEvent(event);

  const fetchEvents = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    const { fromDate, toDate } = getEventsDateRange();
    const eventsProps = {
      UserName: currentUser.LoginName,
      FromDate: fromDate.toISOString().split("T")[0],
      ToDate: toDate.toISOString().split("T")[0],
      Category: [
        "Telehealth",
        "Other",
        "Inperson",
        "Absence",
        "Holidays",
        "Trainings",
        "Quarters",
      ],
    };
    try {
      const response = await getEvents(eventsProps, calendarClient);
      setEvents(response.data || []);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  }, [currentUser, calendarClient]);

  useEffect(() => {
    fetchEvents();
  }, [currentUser, fetchEvents]);

  useEffect(() => {
    if (selectedEvent) {
      fetchMeetingRoomDetails(selectedEvent);
    } else {
      setMeetingRoomDetails(null);
    }
  }, [selectedEvent, fetchMeetingRoomDetails]);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (displayEvents && displayEvents.length > 0) {
      setSelectedEvent(displayEvents[0]);
    } else {
      setSelectedEvent(null);
    }
  }, [selectedDate, displayEvents]);

  useEffect(() => {
    if (selectedEvent) {
      const updated = fullCalendarEvents.find(
        (ev) => ev.Id === selectedEvent.Id && ev.StartTime === selectedEvent.StartTime,
      );
      if (updated) setSelectedEvent(updated);
    }
  }, [events, selectedEvent?.Id, selectedEvent?.StartTime, fullCalendarEvents, selectedEvent]);

  const handleSelectDate = (date: Date) => {
    const selectedDateStart = new Date(date);
    selectedDateStart.setHours(0, 0, 0, 0);
    const eventsForSelectedDate = [...sortedEvents].filter((event) => {
      const eventDate = new Date(event.StartTime!);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate.getTime() === selectedDateStart.getTime();
    });

    if (eventsForSelectedDate.length > 0) {
      setSelectedEvent(eventsForSelectedDate[0]);
    } else {
      const upcomingEvents = [...sortedEvents].filter((event) => {
        const eventDate = new Date(event.StartTime!);
        eventDate.setHours(0, 0, 0, 0);

        return (
          eventDate >= selectedDateStart &&
          eventDate.getTime() !== selectedDateStart.getTime()
        );
      });
      setSelectedEvent(upcomingEvents.length > 0 ? upcomingEvents[0] : null);
    }
    setSelectedDate(new Date(date));
  };

  return (
    <div className="flex flex-col gap-4">
      <Button
        className="self-start rounded"
        onClick={() => window.open("https://calendars.teampbs.com", "_blank")}
      >
        <CalendarIcon className="h-4 w-4 mr-2" />
        Open Calendar App
      </Button>
      <div
        className={`flex flex-wrap pt-6 w-full ${isTablet ? "gap-[50px]" : "gap-5"}`}
        style={isMobile ? { flexDirection: "column" } : {}}
      >
        <div
          className={`flex flex-col gap-4 ${isMobile ? "w-full" : "w-[35%] mr-5"}`}
        >
          <NavigationCalendar
            selectedDate={selectedDate}
            onSelectDate={handleSelectDate}
          />
          {loading && (
            <LoadingSpinner spinnerSize="small" label="Loading events..." />
          )}
          {!loading && (
            <EventTile
              displayEvents={displayEvents}
              groupedEvents={groupedEvents}
              handleJoinMeeting={handleJoinMeeting}
              handleEventCardClick={handleEventCardClick}
            />
          )}
        </div>
        <div
          className={`flex flex-col gap-5 ${isMobile ? "w-full" : "w-[60%]"}`}
        >
          {selectedEvent && (
            <EventDetailCard
              event={selectedEvent}
              loginName={currentUser?.LoginName}
              onRefreshEvents={fetchEvents}
              onJoinMeeting={handleJoinMeeting}
              onBusinessForm={
                meetingRoomDetails?.customProperties?.formURL
                  ? () => handleBusinessFormClick(selectedEvent || {})
                  : undefined
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Calendar;
